import { useMediaQuery } from '@mui/material'
import cx from 'classnames'
import React, { useEffect } from 'react'

import { GENDER_RADIO_INPUT_VALUES } from '@js/common/constants'
import { UA } from '@js/common/UA'
import { validationMessage } from '@js/common/validation'

import CTA from '@f/elements/CTA'
import { Form } from '@f/elements/HookForm'
import yup from '@f/elements/HookForm/validator/yup'

import { conversionFunnelCopy } from '@f-fertighaus/applications/RequestWizard/partials/utils'
import { Gender } from '@f-fertighaus/applications/SalesAgentDashboard/config'
import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

interface GenderNameEmailSchema {
  email: string
  first_name: string
  last_name: string
  gender: string
}

export const formValidationSchema: yup.ObjectSchema<GenderNameEmailSchema> = yup
  .object()
  .shape({
    gender: yup
      .mixed()
      .oneOf([...Gender, ''])
      .required(validationMessage.gender.required),
    first_name: yup.string().firstName(),
    last_name: yup.string().lastName(),
    email: yup.string().emailCustom()
  })

interface UserGenderNameEmailProps {
  actions: $TSFixMe
  data: $TSFixMe
  funnelVariant: ConversionFunnelVariant
}

export type ConversionFunnelVariant =
  | 'house-wizard-item-priceinfo'
  | 'house-wizard-item-infomaterial'
  | 'house-wizard-item-configuration'
  | 'house-wizard-item-offer'
  | 'house-wizard-item-comparison'
  | 'house-wizard-item-catalog'
  | 'house-wizard-item-appointment'
  | 'house-wizard-item-favourite'
  | 'house-wizard-item-expose'

const UserGenderNameEmail = (props: UserGenderNameEmailProps) => {
  const isMobileView = useMediaQuery('(max-width: 767px)')
  useEffect(() => {
    props.actions.update_wtValue({ value: 'gender_name_email' })
  }, [])

  const isConversionFunnel = Boolean(props.funnelVariant)

  const {
    gender,
    first_name,
    last_name,
    email
  }: {
    first_name: string
    last_name: string
    email: string
    gender: string
  } = props.data.user

  const handleSubmit = (values: GenderNameEmailSchema) => {
    const detail = {
      ...values,
      receive_newsletter: true
    }

    props.actions.handleUserDataChange({ detail })
    props.actions.move({})
  }

  const manageProgressBarDisplay = () => {
    props.actions.hideProgressBar()

    return props.actions.showProgressBar
  }

  useEffect(manageProgressBarDisplay, [])

  const ctaText = isConversionFunnel
    ? conversionFunnelCopy[props.funnelVariant].step_1.ctaText
    : 'Jetzt Konto erstellen'

  const submitButton = (
    <CTA
      type='block-primary'
      tag='button'
      buttonType='submit'
      data-sel='btn-cta'
      className='is-block:s'
      wtData={{
        'CTA-click': {
          ...props.data.trackingInfo
        }
      }}>
      {ctaText}
    </CTA>
  )

  const disclaimerParagraph = (
    <p className='[ f-t-micro f-c-fo-neu200 f-m-b-020 f-d-bl f-t-centre ] RequestWizard-bottom'>
      {`Mit einem Klick auf den Button „${ctaText}“ bestätigen Sie, dass Sie unsere `}
      <CTA
        type='text-primary'
        tag='a'
        href='/datenschutz/'
        target='_blank'
        className='internal'>
        Datenschutzerklärung
      </CTA>{' '}
      zur Kenntnis genommen haben. Kein Spam, versprochen.
    </p>
  )

  const defaultValues = {
    first_name,
    last_name,
    email,
    gender
  }

  return (
    <Form.Wrapper<GenderNameEmailSchema>
      className={cx(
        'RequestWizard-form-wrapper',
        isConversionFunnel &&
          'RequestWizard-form-wrapper--HDP_conversion-funnel'
      )}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      validationSchema={formValidationSchema}
      wtData={{
        form: {
          location: 'gender_name_email',
          indicates: ['activation'],
          ...props.data.trackingInfo
        }
      }}
      data-wtloc='modal-gender-name'>
      <div className='RequestWizard-top'>
        <div className='RequestWizard-heading'>
          <div className='[ f-t-tmedium f-c-fo-pri f-t-centre f-p-h-080:m ]'>
            {isConversionFunnel
              ? conversionFunnelCopy[props.funnelVariant].step_1.header
              : 'Jetzt Konto erstellen und kostenlose Hausinfos erhalten'}
          </div>
        </div>

        <div className='RequestWizard-content'>
          <div className='f-d-flr f-fl-al-ce f-fl-wrap f-m-b-020 is-full-width'>
            <div className='f-t-body '>Anrede:</div>
            <Form.Radio
              radioTheme='basic'
              radioClassName='[ f-c-ba-whi f-c-fo-pri f-c-bo-neu050 f-m-l-040 ]'
              radioLabelClassName='f-m-l-010'
              radioErrorFullWidth
              className='f-d-flr'
              name='gender'
              radioOptions={GENDER_RADIO_INPUT_VALUES}
              wtData={{
                radiobutton: {
                  location: 'gender_name_email',
                  ...props.data.trackingInfo
                }
              }}
            />
          </div>
          <Form.Text
            name='first_name'
            className='[ f-c-bo-neu050 ] has-border'
            placeholder='Vorname'
            inputProps={{ 'data-sel': 'ipt-fname' }}
          />
          <Form.Text
            name='last_name'
            className='[ f-c-bo-neu050 ] has-border'
            placeholder='Nachname'
            inputProps={{ 'data-sel': 'ipt-lname' }}
          />
          <Form.Text
            name='email'
            className='[ f-c-bo-neu050 ] has-border is-full-width'
            placeholder='E-Mail-Adresse'
            inputProps={{
              'data-sel': 'ipt-email',
              inputMode: 'email'
            }}
            disabled={Boolean(UA.isLoggedIn() && email)}
          />
        </div>
        <div className='f-t-centre f-m-v-040:m f-m-b-020'>
          {isMobileView ? disclaimerParagraph : submitButton}
        </div>
      </div>
      {isMobileView ? submitButton : disclaimerParagraph}
    </Form.Wrapper>
  )
}

export default UserGenderNameEmail
